import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Grid, Spinner, Typography } from '@kc-console/common-ui';
import { np, t } from '@kc-console/core';
import {
  IllustStatusLoadFail,
  IllustStatusResourceNone,
  IllustStatusSearch,
} from '@kep/kcds-icons';

import { EMPTY_STATE } from './types';

import type {
  EmptyStateConfig,
  EmptyStateProps,
  EmptyStateSize,
  EmptyStateType,
  WarningEmptyStateConfig,
} from './types';

const ILLUSTRATION_SIZE: Record<EmptyStateSize, number> = {
  md: 72,
  lg: 100,
};

const getDefaultEmptyStateConfig = (): Record<EmptyStateType, EmptyStateConfig> => ({
  [EMPTY_STATE.LOADING]: {
    illustration: <Spinner size="lg" />,
    message: undefined,
    guideText: undefined,
    action: undefined,
  },
  [EMPTY_STATE.LIST]: {
    illustration: <IllustStatusResourceNone />,
    message: t('ui:empty_state.no_resources'),
    guideText: undefined,
    action: undefined,
  },
  [EMPTY_STATE.SEARCH]: {
    illustration: <IllustStatusSearch />,
    message: t('common:common.list.no_matching_items'),
    guideText: undefined,
    action: undefined,
  },
  [EMPTY_STATE.WARNING]: {
    illustration: <IllustStatusLoadFail />,
    message: t('ui:error.temporary_load_error'),
    guideText: t('ui:error.refresh_instruction'),
    action: undefined,
  },
});

const getEmptyStateConfig = (type: EmptyStateType, options: WarningEmptyStateConfig) => {
  const { onRefetch } = options;
  const config = getDefaultEmptyStateConfig()[type];

  if (type === EMPTY_STATE.WARNING && onRefetch) {
    return {
      ...config,
      action: (
        <Button key="refresh" variant="secondary" size="sm" onClick={onRefetch}>
          {t('ui:action.refresh')}
        </Button>
      ),
    };
  }

  return config;
};

export default function EmptyState(props: EmptyStateProps) {
  const {
    type,
    size = 'lg',
    onRefetch,
    illustration: customIllustration,
    message: customMessage,
    guideText: customGuideText,
    action: customAction,
    ...others
  } = props;

  const config = getEmptyStateConfig(type, { onRefetch });
  const illustration = customIllustration !== undefined ? customIllustration : config.illustration;
  const message = customMessage !== undefined ? customMessage : config.message;
  const guideText = customGuideText !== undefined ? customGuideText : config.guideText;
  const action = customAction !== undefined ? customAction : config.action;

  return (
    <Grid container flexDirection="column" alignItems="center" {...others}>
      {illustration && <IllustrationContainer size={size}>{illustration}</IllustrationContainer>}
      {message && (
        <MessageContainer color="text/weakest/default" variant="label/medium/lg">
          {message}
        </MessageContainer>
      )}
      {guideText && (
        <GuideTextContainer color="text/weakest/default" variant="label/medium/md">
          {guideText}
        </GuideTextContainer>
      )}
      {action && <ActionContainer>{action}</ActionContainer>}
    </Grid>
  );
}

const IllustrationContainer = styled('div', { shouldForwardProp: np('size') })<{
  size: EmptyStateSize;
}>(
  ({ size }) => css`
    margin-bottom: 8px;

    > svg {
      width: ${ILLUSTRATION_SIZE[size]}px;
      height: ${ILLUSTRATION_SIZE[size]}px;
    }
  `,
);
const MessageContainer = styled(Typography)`
  max-width: 39;
`;

const GuideTextContainer = styled(Typography)(
  () => css`
    margin-top: 8px;
  `,
);

const ActionContainer = styled('div')(
  () => css`
    margin-top: 16px;
  `,
);
